<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div>
            <h4 class="mb-1">광고관리</h4>
            <p class="mb-0 text-muted">성과는 실제로 받은 돈(할인 적용가)과 실제로 낸 광고비(부가세 포함) 기준 · 추천 목표ROAS는 쿠팡 입력칸에 그대로 넣는 값</p>
          </div>
          <div class="d-flex align-items-center">
            <b-form-input v-model="startDate" type="date" class="mr-2" style="width:140px;" />
            <span class="mx-1">~</span>
            <b-form-input v-model="endDate" type="date" class="mr-2" style="width:140px;" />
            <b-button variant="primary" @click="search" :disabled="loading" class="px-3">
              <i class="fas fa-search mr-1"></i>조회
            </b-button>
            <b-button variant="outline-success" @click="exportExcel" class="ml-2 px-3" :disabled="!showData">
              <i class="fas fa-file-excel mr-1"></i>엑셀
            </b-button>
          </div>
        </div>
      </div>

      <div class="col-12 text-center py-5" v-if="loading">
        <b-spinner variant="primary" />
        <p class="mt-2">{{ loadingText }}</p>
      </div>

      <template v-if="showData">
        <!-- 이 계정의 광고 데이터가 아직 없음 (계정 분리 후 재수집 전 상태 포함) -->
        <div class="col-12" v-if="!hasAnyAdData">
          <div class="card">
            <div class="card-body text-center py-5">
              <i class="fas fa-inbox fa-2x text-muted d-block mb-3"></i>
              <h5>이 계정의 광고 데이터가 아직 없어요</h5>
              <p class="text-muted mb-1">광고 데이터는 쿠팡 계정별로 분리해서 보여드려요.</p>
              <p class="text-muted mb-0">판매장부에서 <b>정산현황수집</b>을 실행하면 현재 계정 기준으로 캠페인·광고 성과가 수집됩니다.</p>
            </div>
          </div>
        </div>

        <!-- ① 캠페인별 테이블 — 현재 상황(실측) + 방향성(권장)을 한 행에 -->
        <div class="col-12 mb-3" v-if="hasAnyAdData">
          <div class="card mb-0">
            <div class="card-header py-2">
              <strong>캠페인별 현황 · 권장</strong>
              <small class="text-muted ml-1">행을 클릭하면 캠페인 상세(게이지·상품·할 일)가 열려요</small>
              <div class="mt-1"><small class="text-muted">
                <i class="fas fa-info-circle mr-1"></i>
                <b>광고ROI</b> 100% = 본전(넘으면 흑자) · <b>권장 목표</b>의 화살표 값을 쿠팡에 그대로 입력하세요 ·
                상품·키워드별 상세는 <b>광고관리(소싱검증)</b> 메뉴에서 확인하세요
              </small></div>
            </div>
            <div class="card-body p-0" v-if="headlines.length">
              <CDataTable
                storage-key="growthAdManager"
                :headers="campaignHeaders"
                :items="headlines"
                :show-select="false"
                itemKey="campaignId"
                :items-per-page="20"
                :pagination="true"
                :headerTooltips="campaignHeaderTooltips"
              >
                <template v-slot:name="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;">
                    <small><strong>{{ item.name }}</strong> <span :class="item.isActive ? 'text-success' : 'text-danger'">{{ item.isActive ? 'ON' : 'OFF' }}</span></small>
                  </div>
                </template>
                <template v-slot:campaignType="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;"><small><span class="badge badge-secondary">{{ item.campaignType }}</span></small></div>
                </template>
                <template v-slot:_status="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;"><small><span v-for="(c, ci) in headlineStatus(item)" :key="ci" class="badge mr-1" :class="c.variant">{{ c.text }}</span></small></div>
                </template>
                <template v-slot:_perf="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;" class="text-right">
                    <small class="d-block">{{ formatNum((item.totalCost || 0) * 1.1) }}원 / {{ formatNum(item.totalDiscRevenue || item.totalSales || 0) }}원</small>
                    <small class="text-muted">전환 {{ item.totalOrders || 0 }}건</small>
                  </div>
                </template>
                <template v-slot:adRoi="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;">
                    <small v-if="isLowData(item)" class="text-muted">-</small>
                    <small v-else :class="adRoiColor(adRoiPct(item))">{{ adRoiPct(item) }}%
                      <i class="fas" :class="adRoiPct(item) >= 100 ? 'fa-check-circle' : 'fa-exclamation-circle'" :title="adRoiPct(item) >= 100 ? '흑자' : '적자'"></i>
                    </small>
                  </div>
                </template>
                <template v-slot:_natural="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;" class="text-center">
                    <small v-if="naturalPullByCid[String(item.campaignId)]" :class="naturalPullClass(naturalPullByCid[String(item.campaignId)])">
                      {{ naturalPullByCid[String(item.campaignId)].arrow }} {{ naturalPullByCid[String(item.campaignId)].pct }}%
                    </small>
                    <small v-else class="text-muted">-</small>
                  </div>
                </template>
                <template v-slot:_rec="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;" :class="{ 'text-muted': item.marginEstimated || isBlocked(item) || !item.isActive }">
                    <small v-if="item.campaignType === '매출스타트'" class="text-muted" title="매출스타트는 수익률 목표 없이 예산을 공격적으로 쓰는 노출 활성화 광고예요 — 일예산이 사실상의 한도">
                      수익률 목표 없음 · 일예산이 한도
                    </small>
                    <small v-else-if="item.campaignType === '신규고객확보'" class="text-muted" title="신규 구매 고객 1명당 목표 비용으로 운영되는 별도 유형 — 광고센터에서 관리">
                      신규고객 1명당 비용으로 운영
                    </small>
                    <small v-else-if="item.campaignType === '기타'" class="text-muted" title="이 광고 유형은 아직 권장값 계산을 지원하지 않아요">-</small>
                    <small v-else-if="!item.isActive" class="text-muted">
                      꺼짐<span v-if="!isManualType(item.campaignType) && recInputPct(item) > 0"> · 켜면 ROAS {{ recInputPct(item) }}%</span>
                    </small>
                    <small v-else-if="isBlocked(item)" class="text-muted" title="송출 불가·학습 정체 상태 — 설정값보다 상태 해결이 먼저">
                      <i class="fas fa-exclamation-triangle text-warning mr-1"></i>상태 점검 먼저
                    </small>
                    <small v-else-if="isLowData(item)" class="text-muted" :title="'전환 ' + (item.totalOrders||0) + '건·클릭 ' + (item.totalClicks||0) + '건 (판단 기준: 전환 3·클릭 11)'">
                      데이터 부족 — 더 모은 뒤 판단 (전환 {{ item.totalOrders || 0 }}/3)
                    </small>
                    <small v-else-if="isManualType(item.campaignType)">검색CPC <strong>{{ recCpc(item) }}</strong>원</small>
                    <small v-else>목표ROAS
                      <span :class="recArrowClass(item)">{{ item.currentRoasTarget || '-' }}% → <strong>{{ recInputPct(item) }}%</strong> {{ recArrow(item) }}</span>
                    </small>
                    <span v-if="['AI광고', '매출최적화', '수동키워드', '수동스마트타겟팅'].includes(item.campaignType) && item.marginEstimated" class="badge badge-light border text-warning ml-1" title="매입 원가 미입력 — 기본 마진으로 추정, 추천 신뢰 낮음">원가입력필요</span>
                  </div>
                </template>
                <template v-slot:_actions="{item}">
                  <div v-if="item.campaignType === '기타' || item.campaignType === '신규고객확보'" class="text-center"><small class="text-muted">-</small></div>
                  <div v-else-if="(actionChipsByCid[String(item.campaignId)] || []).length">
                    <span v-for="chip in actionChipsByCid[String(item.campaignId)]" :key="chip.key"
                          class="badge mr-1 mb-1 action-chip" :class="actionBadgeClass(chip.severity)"
                          :title="chip.title" @click="goToCampaign(item.campaignId)">{{ chip.text }}</span>
                  </div>
                  <div v-else class="text-center"><small class="text-muted">-</small></div>
                </template>
                <template v-slot:budget="{item}">
                  <div @click="openCampaignModal(item)" style="cursor:pointer;"><small>{{ formatNum(item.budget) }} <span :class="item.budgetUtilRate >= 100 ? 'text-danger' : 'text-muted'">({{ item.budgetUtilRate || '-' }}%)</span></small></div>
                </template>
                <template v-slot:_memo="{item}">
                  <MemoCell :content="getCampaignMemo(item)" @save="(c) => saveCampaignMemo(item, c)" />
                </template>
              </CDataTable>
            </div>
            <div class="card-body text-center text-muted py-4" v-else>
              <small>캠페인 데이터가 아직 없어요 — 판매장부에서 정산현황수집을 실행하면 채워져요</small>
            </div>
          </div>
        </div>

        <!-- 캠페인 상세 모달 — 한 캠페인의 모든 것 (할 일·게이지·상품 판단·일별 추이) -->
        <b-modal v-model="showCampaignModal" size="xl" hide-footer scrollable
                 :title="selectedCampaign ? selectedCampaign.name + ' — 캠페인 상세' : ''"
                 @shown="campaignChartReady = true" @hidden="campaignChartReady = false">
          <template v-if="selectedCampaign">
            <div class="d-flex align-items-center flex-wrap mb-3">
              <span class="badge badge-secondary mr-2">{{ selectedCampaign.campaignType }}</span>
              <span class="mr-2 font-weight-bold" :class="selectedCampaign.isActive ? 'text-success' : 'text-danger'">{{ selectedCampaign.isActive ? 'ON' : 'OFF' }}</span>
              <span v-for="(c, ci) in headlineStatus(selectedCampaign)" :key="ci" class="badge mr-1" :class="c.variant">{{ c.text }}</span>
              <span class="ml-auto">
                <small class="text-muted mr-1" title="광고로 번 마진 ÷ 광고비 · 100%가 본전">광고ROI</small>
                <strong v-if="!isLowData(selectedCampaign)" :class="adRoiColor(adRoiPct(selectedCampaign))" style="font-size:18px;">{{ adRoiPct(selectedCampaign) }}%</strong>
                <small v-else class="text-muted">데이터 부족</small>
                <small class="text-muted ml-3">예산 {{ formatNum(selectedCampaign.budget) }}원 · 소진 {{ selectedCampaign.budgetUtilRate || '-' }}%</small>
              </span>
            </div>

            <div class="card border-primary mb-3">
              <div class="card-header py-1 bg-primary text-white"><small class="font-weight-bold"><i class="fas fa-bullseye mr-1"></i>이 캠페인에서 할 일</small></div>
              <div class="card-body p-2">
                <div v-if="!campaignActions.length" class="text-muted text-center py-2"><small><i class="fas fa-check-circle text-success mr-1"></i>지금 바로 조치할 항목이 없어요</small></div>
                <div v-for="(a, ai) in campaignActions" :key="a.key" :class="{ 'border-top pt-2 mt-2': ai > 0 }">
                  <span class="badge mr-1" :class="actionBadgeClass(a.severity)">{{ a.badge }}</span>
                  <small class="font-weight-bold">{{ actionVerb(a) }}</small>
                  <div><small class="text-muted">{{ a.reason }}</small></div>
                  <div class="mt-1 d-flex align-items-center flex-wrap">
                    <span class="action-pill"><i class="fas fa-arrow-right mr-1"></i>{{ a.action }}</span>
                    <b-button v-if="!isApplied(a)" size="sm" variant="outline-success" class="ml-2 py-0" @click="markActionApplied(a)" title="쿠팡에 적용했다면 눌러서 캠페인 메모에 기록">적용함</b-button>
                    <small v-else class="ml-2 text-success"><i class="fas fa-check mr-1"></i>메모에 기록함</small>
                  </div>
                </div>
                <div class="border-top pt-2 mt-2 text-center">
                  <small class="text-muted">적용은 쿠팡 광고센터 &gt; 광고 관리에서 캠페인명으로 찾아 입력하세요 —
                    <a href="https://advertising.coupang.com" target="_blank" rel="noopener">광고센터 열기 <i class="fas fa-external-link-alt"></i></a>
                  </small>
                </div>
              </div>
            </div>

            <!-- 상태가 막혀 있으면 권장값보다 상태 해결이 먼저 — 게이지 대신 안내 (권장 게이지와 '끄기' 할 일이 충돌하지 않게) -->
            <div v-if="modalSpectrum && (!selectedCampaign.isActive || isBlocked(selectedCampaign) || isLowData(selectedCampaign))" class="alert alert-light border mb-3">
              <small>
                <i class="fas fa-exclamation-triangle text-warning mr-1"></i>
                <span v-if="!selectedCampaign.isActive">꺼진 캠페인이에요. 다시 켤지부터 정하세요</span>
                <span v-else-if="isBlocked(selectedCampaign)">송출 불가·학습 정체 상태예요. 권장값을 바꾸기 전에 위 "할 일"의 상태부터 해결하세요</span>
                <span v-else>판단할 데이터가 부족해요 (전환 {{ selectedCampaign.totalOrders || 0 }}/3). 더 모인 뒤 권장값이 나와요</span>
                <span v-if="!isManualType(selectedCampaign.campaignType) && recInputPct(selectedCampaign) > 0"> · 참고로 정상화되면 권장 목표ROAS는 {{ recInputPct(selectedCampaign) }}%</span>
              </small>
            </div>
            <!-- §15 ROAS/CPC 스펙트럼 게이지 -->
            <div v-else-if="modalSpectrum" class="roas-gauge mb-3 px-2">
              <div class="d-flex justify-content-between mb-1">
                <small class="text-danger"><i class="fas fa-bolt mr-1"></i>공격(손익분기)</small>
                <small class="text-primary">최적 · 순이익 최대</small>
                <small class="text-success">최대효율 · 보존</small>
              </div>
              <div class="gauge-track">
                <div class="gauge-opt" :style="{ left: modalSpectrum.optPos + '%' }" title="최적(순이익 최대)"></div>
                <div v-if="modalSpectrum.curPos !== null" class="gauge-marker gauge-cur" :style="{ left: modalSpectrum.curPos + '%' }">
                  <span class="gauge-label">현재 {{ modalSpectrum.curVal }}%</span>
                </div>
                <div v-if="modalSpectrum.recPos !== null" class="gauge-marker gauge-rec" :style="{ left: modalSpectrum.recPos + '%' }">
                  <span class="gauge-label">권장 {{ modalSpectrum.manual ? modalSpectrum.recCpc + '원' : modalSpectrum.recVal + '%' }}</span>
                </div>
              </div>
              <div class="d-flex justify-content-between mt-1 text-muted">
                <small>{{ modalSpectrum.brk }}%</small>
                <small>{{ modalSpectrum.opt }}%</small>
                <small>{{ modalSpectrum.mx }}%</small>
              </div>
              <div class="text-center mt-1"><small class="text-muted">
                <i class="fas fa-info-circle mr-1"></i>왼쪽으로 갈수록 공격(노출↑·이익↓), 오른쪽일수록 효율(이익↑·노출↓) · 권장은 시작값일 뿐, 결과 보고 대응
              </small></div>
            </div>

            <!-- 상품별 판단 (§18: 분리/제외 중심) -->
            <div class="card mb-3">
              <div class="card-header py-1"><small class="font-weight-bold">상품별 판단</small></div>
              <div class="card-body p-2">
                <div class="table-responsive" v-if="selectedCampaign.products && selectedCampaign.products.length">
                  <table class="table table-sm table-borderless mb-0">
                    <thead><tr class="text-muted">
                      <th><small>상품</small></th>
                      <th class="text-center"><small>판단</small></th>
                      <th class="text-right"><small>광고ROI</small></th>
                      <th class="text-right"><small>전환</small></th>
                      <th class="text-center" title="품질점수 기준 — 전환 30건 채우면 1차 통과, 60건이 권장"><small>최근 30일 전환</small></th>
                      <th><small>배송/재고</small></th>
                      <th title="쿠팡 입력은 캠페인 단위예요. 이 값은 상품을 빼거나(제외) 따로 돌릴지(분리) 판단하는 참고치 — 재고 상황 반영"><small>상품별 적정선 (참고)</small></th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="p in selectedCampaign.products" :key="p.vendorItemId">
                        <td><small>{{ productName(p.vendorItemId) }}</small>
                          <span v-if="p.coverageMode === '발주공격' || selectedCampaign.chargeGate === '충전필요'" class="badge badge-warning ml-1">⚡공격여지</span>
                        </td>
                        <td class="text-center text-nowrap"><small :class="productVerdict(p, selectedCampaign).cls">{{ productVerdict(p, selectedCampaign).text }}</small></td>
                        <td class="text-right">
                          <small v-if="(p.orders || 0) === 0" class="text-muted" title="전환 0 — 효율을 판단할 데이터가 없어요 (0%로 측정된 게 아님)">-</small>
                          <small v-else :class="adRoiColor(Math.round(p.adRoi * 100))">{{ Math.round(p.adRoi * 100) }}%</small>
                        </td>
                        <td class="text-right"><small>{{ p.orders }}건</small></td>
                        <td class="text-center"><small><span class="badge" :class="chargeBadge(p.conv30)">{{ p.conv30 }}/60</span></small></td>
                        <td><small>{{ productStock(p) }}</small></td>
                        <td :class="{ 'text-muted': p.marginEstimated }"><small>{{ productRec(p, selectedCampaign.campaignType) }}</small>
                          <span v-if="p.marginEstimated" class="badge badge-light border text-warning ml-1" title="매입 원가 미입력 — 기본 마진으로 추정">원가입력필요</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <small v-else class="text-muted">상품 데이터 없음 (매핑/수집 후 표시)</small>
              </div>
            </div>

            <!-- 일별 추이 — 광고비·광고매출(+자연매출, 데이터 있으면) -->
            <div class="card mb-3" v-if="campaignModalRows.length">
              <div class="card-header py-1"><small class="font-weight-bold">일별 추이 — 광고비·광고매출{{ campaignModalHasNatural ? '·자연매출' : '' }}</small></div>
              <div class="card-body py-2">
                <ApexChart v-if="campaignChartReady" :key="'cmodal-' + selectedCampaign.campaignId" element="campaign-modal-chart" :chartOption="campaignModalChartOptions" />
              </div>
            </div>

            <!-- 캠페인 메모 — 작업 일지 (기록 장치, 회사 공유) -->
            <div class="card mb-0">
              <div class="card-header py-1"><small class="font-weight-bold"><i class="fas fa-pen mr-1"></i>캠페인 메모 — 무엇을 왜 바꿨는지 남겨두면 결과 해석이 쉬워져요</small></div>
              <div class="card-body p-2">
                <MemoCell :content="getCampaignMemo(selectedCampaign)" @save="(c) => saveCampaignMemo(selectedCampaign, c)" placeholder="예: 06/10 목표ROAS 400→576% 올림 (권장 적용)" />
              </div>
            </div>
          </template>
        </b-modal>

        <!-- ② SKU(상품)별 테이블 — 영역(검색·비검색·오디언스)별로 뭘 할지 결과 먼저 -->
        <div class="col-12 mb-3" v-if="hasAnyAdData">
          <div class="card mb-0">
            <div class="card-header py-2 d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <strong>상품(옵션)별 현황 · 권장</strong>
                <small class="text-muted ml-1">행을 클릭하면 영역별 상세가 열려요</small>
                <div class="mt-1"><small class="text-muted">
                  <i class="fas fa-info-circle mr-1"></i>
                  이 표는 상품(옵션)별로 영역(검색·비검색·오디언스) 성과를 묶어, 영역마다 뭘 할지 보여줘요. 행을 누르면 상세가 열려요 ·
                  전환·클릭이 적은 영역은 신뢰가 낮으니 참고로만 보세요
                </small></div>
              </div>
              <b-form-select v-if="hasSkuPerformance" v-model="skuFilterCid" :options="skuFilterOptions" size="sm" style="width:240px;" />
            </div>
            <div class="card-body p-0" v-if="hasSkuPerformance && skuRows.length">
              <CDataTable
                storage-key="growthAdManagerSku"
                :headers="skuHeaders"
                :items="skuRows"
                :show-select="false"
                itemKey="vendorItemId"
                :items-per-page="20"
                :pagination="true"
                :headerTooltips="skuHeaderTooltips"
              >
                <template v-slot:_product="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;">
                    <small class="d-block"><strong>{{ productName(item.vendorItemId) }}</strong></small>
                    <small class="text-muted">{{ item.vendorItemId }}</small>
                  </div>
                </template>
                <template v-slot:_campaigns="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;"><small>{{ skuCampaignLabel(item) }}</small></div>
                </template>
                <template v-slot:_cost="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;" class="text-right"><small>{{ formatNum((item.totalCost || 0) * 1.1) }}원</small></div>
                </template>
                <template v-slot:_sales="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;" class="text-right"><small>{{ formatNum(item.totalSales || 0) }}원</small></div>
                </template>
                <template v-slot:_orders="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;" class="text-right"><small>{{ item.totalOrders || 0 }}건</small></div>
                </template>
                <template v-slot:_adRoi="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;" class="text-right">
                    <small v-if="(item.totalOrders || 0) === 0" class="text-muted" title="전환 0 — 효율을 판단할 데이터가 없어요">-</small>
                    <small v-else :class="adRoiColor(Math.round((item.adRoi || 0) * 100))">{{ Math.round((item.adRoi || 0) * 100) }}%</small>
                  </div>
                </template>
                <template v-slot:_strategy="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;">
                    <template v-if="skuStrategyChips(item).length">
                      <span v-for="chip in skuStrategyChips(item)" :key="chip.area" class="badge mr-1 mb-1" :class="strategyClass(chip.strategy)">{{ chip.text }}</span>
                    </template>
                    <small v-else class="text-muted">데이터 부족</small>
                  </div>
                </template>
                <template v-slot:_continue="{item}">
                  <div @click="openSkuModal(item)" style="cursor:pointer;" class="text-center">
                    <small :class="item.continue ? 'text-success' : 'text-danger'">
                      <i class="fas" :class="item.continue ? 'fa-check-circle' : 'fa-times-circle'"></i>
                      {{ item.continue ? '가능' : '주의' }}
                    </small>
                  </div>
                </template>
              </CDataTable>
            </div>
            <div class="card-body text-center text-muted py-4" v-else-if="hasSkuPerformance">
              <small>표시할 상품 성과가 아직 없어요 — 수집/매핑 후 채워져요</small>
            </div>
            <div class="card-body text-center text-muted py-4" v-else>
              <i class="fas fa-cubes fa-2x d-block mb-2"></i>
              <p class="mb-1">상품(옵션)별 성과는 다음 수집/배포 후 표시됩니다.</p>
              <small>준비되면 이 자리에 영역(검색·비검색·오디언스)별 권장이 함께 보여요.</small>
            </div>
          </div>
        </div>

        <!-- ② SKU 상세 모달 — 한 상품의 영역별 성과 매트릭스 + 영역별 광고전략 -->
        <b-modal v-model="showSkuModal" size="xl" hide-footer scrollable
                 :title="selectedSku ? productName(selectedSku.vendorItemId) + ' — 상품 상세' : ''">
          <template v-if="selectedSku">
            <div class="d-flex align-items-center flex-wrap mb-2">
              <span class="mr-3"><small class="text-muted">광고비</small> <strong>{{ formatNum((selectedSku.totalCost || 0) * 1.1) }}원</strong></span>
              <span class="mr-3"><small class="text-muted">매출</small> <strong>{{ formatNum(selectedSku.totalSales || 0) }}원</strong></span>
              <span class="mr-3"><small class="text-muted">전환</small> <strong>{{ selectedSku.totalOrders || 0 }}건</strong></span>
              <span class="mr-3"><small class="text-muted" title="광고로 번 마진 ÷ 광고비 · 100%가 본전">광고ROI</small>
                <strong v-if="(selectedSku.totalOrders || 0) > 0" :class="adRoiColor(Math.round((selectedSku.adRoi || 0) * 100))">{{ Math.round((selectedSku.adRoi || 0) * 100) }}%</strong>
                <small v-else class="text-muted">데이터 부족</small>
              </span>
              <span class="mr-3"><small class="text-muted">판매지속</small>
                <strong :class="selectedSku.continue ? 'text-success' : 'text-danger'">{{ selectedSku.continue ? '가능' : '주의' }}</strong>
              </span>
              <span v-if="selectedSku.marginEstimated" class="badge badge-light border text-warning ml-1" title="매입 원가 미입력 — 기본 마진으로 추정">원가입력필요</span>
            </div>
            <div class="mb-3">
              <small class="text-muted mr-1">걸린 캠페인</small>
              <span v-for="(cn, ci) in (selectedSku.campaignNames || [])" :key="ci" class="badge badge-secondary mr-1">{{ cn }}</span>
              <span v-if="!(selectedSku.campaignNames || []).length"><small class="text-muted">-</small></span>
            </div>

            <!-- 영역별 광고전략 결론 (강조) -->
            <div class="row mb-3">
              <div v-for="col in skuAreaColumns" :key="'concl-' + col.key" class="col-md-4 mb-2">
                <div class="card h-100">
                  <div class="card-body p-2">
                    <div class="d-flex align-items-center justify-content-between">
                      <small class="font-weight-bold">{{ col.label }}</small>
                      <span v-if="col.area" class="badge" :class="strategyClass(col.area.strategy)">{{ col.area.strategy || '-' }}</span>
                      <small v-else class="text-muted">데이터 없음</small>
                    </div>
                    <div v-if="col.area" class="mt-1">
                      <small class="d-block">권장 CPC(최적) <strong>{{ formatNum(col.area.cpcOpt) }}</strong>원</small>
                      <small class="d-block text-muted">CPC 한도 {{ formatNum(col.area.cpcUsl) }}원</small>
                      <small v-if="!col.area.validated" class="text-muted">참고치 — 데이터 적음</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 영역별 상세 매트릭스 (지표=행, 영역=열) -->
            <div class="card mb-0">
              <div class="card-header py-1"><small class="font-weight-bold">영역별 상세 — 지표를 영역(검색·비검색·오디언스)별로</small></div>
              <div class="card-body p-2">
                <div class="table-responsive">
                  <table class="table table-sm table-borderless mb-0 sku-area-table">
                    <thead><tr class="text-muted">
                      <th><small>지표</small></th>
                      <th v-for="col in skuAreaColumns" :key="'h-' + col.key" class="text-right"><small>{{ col.label }}</small></th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="row in skuAreaMetrics" :key="row.key">
                        <td><small :title="row.hint">{{ row.label }}</small></td>
                        <td v-for="col in skuAreaColumns" :key="row.key + '-' + col.key" class="text-right">
                          <small :class="row.cls ? row.cls(col.area) : ''">{{ col.area ? row.fmt(col.area) : '-' }}</small>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="mt-2"><small class="text-muted">
                  <i class="fas fa-info-circle mr-1"></i>CPC 한도를 넘기면 적자, 최적 CPC가 순이익이 가장 큰 시작값이에요. 전환·클릭이 적은 영역(검증 미완료)은 참고로만 보세요.
                </small></div>
              </div>
            </div>
          </template>
        </b-modal>

        <!-- ③ 날짜별 변경 & 추이 — 변경 이벤트가 찍힌 일별 차트 + 테이블 -->
        <div class="col-12 mb-3" v-if="hasAnyAdData">
          <div class="card mb-0">
            <div class="card-header py-2 d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <strong>날짜별 변경 &amp; 추이</strong>
                <small class="text-muted ml-1">언제 뭘 바꿨고, 그 뒤 광고·자연 매출이 어떻게 움직였는지</small>
              </div>
              <b-form-select v-if="hasHistory" v-model="historyFilterCid" :options="historyFilterOptions" size="sm" style="width:240px;" />
            </div>
            <div class="card-body">
              <template v-if="hasHistory">
                <template v-if="historyRows.length">
                  <div style="min-height:340px;">
                    <ApexChart v-if="chartReady" :key="'history-' + chartKey" element="ad-history-chart" :chartOption="historyChartOptions" />
                  </div>
                  <div class="text-center mb-2"><small class="text-muted">
                    <i class="fas fa-info-circle mr-1"></i>광고ROAS는 그대로인데 전체ROAS·자연 노출이 오르면, 광고가 자연 판매를 끌어올리는 중이에요 — 광고 자체가 본전이어도 유지할 가치가 있어요 (참고치)
                  </small></div>
                  <div class="text-center mb-2" v-if="needExposureHint"><small class="text-muted">
                    <i class="fas fa-lightbulb mr-1"></i>노출추적 메뉴에서 광고 상품을 등록하고 수집하면 자연 노출 변화가 함께 보여요
                    (지금은 광고 상품 {{ (history.exposureCoverage || {}).advertised || 0 }}개 중 {{ (history.exposureCoverage || {}).tracked || 0 }}개만 추적 중)
                  </small></div>
                  <CDataTable
                    storage-key="growthAdManagerHistory"
                    :headers="historyHeaders"
                    :items="historyRows"
                    :show-select="false"
                    itemKey="date"
                    :items-per-page="31"
                    :headerTooltips="historyHeaderTooltips"
                  >
                    <template v-slot:date="{item}">
                      <small :class="{ 'font-weight-bold': item.changes.length > 0 }">{{ formatDate(item.date) }}</small>
                    </template>
                    <template v-slot:_changes="{item}">
                      <div v-for="ch in item.changes" :key="ch.field + ch.campaignId" class="text-nowrap">
                        <small class="text-danger font-weight-bold">{{ formatChange(ch) }}</small>
                      </div>
                      <small v-if="!item.changes.length" class="text-muted">-</small>
                    </template>
                    <template v-slot:cost="{item}"><small>{{ formatNum(item.cost) }}</small></template>
                    <template v-slot:adSales="{item}"><small>{{ formatNum(item.adSales) }}</small></template>
                    <template v-slot:naturalSales="{item}"><small>{{ formatNum(item.naturalSales) }}</small></template>
                    <template v-slot:totalRoas="{item}">
                      <small v-if="item.totalRoas !== null" class="font-weight-bold">{{ item.totalRoas }}%</small>
                      <small v-else class="text-muted" title="광고비가 미미한 날은 비율이 의미 없어요">-</small>
                    </template>
                    <template v-slot:adRoas="{item}">
                      <small v-if="item.adRoas !== null">{{ item.adRoas }}%</small>
                      <small v-else class="text-muted">-</small>
                    </template>
                    <template v-slot:exposure="{item}"><small>{{ item.exposure === null ? '-' : formatNum(item.exposure) }}</small></template>
                  </CDataTable>
                </template>
                <div v-else class="text-center text-muted py-4">
                  <small>해당 기간의 일별 데이터가 없어요 — 판매장부에서 정산현황수집을 실행하면 채워져요</small>
                </div>
              </template>
              <div v-else class="text-center text-muted py-4">
                <i class="fas fa-history fa-2x d-block mb-2"></i>
                <p class="mb-1">날짜별 변경·추이는 다음 수집/배포 후 표시됩니다.</p>
                <small>준비되면 이 자리에 일별 광고·자연 매출 추이와 변경 기록이 함께 보여요.</small>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ApexChart from '@/components/charts/ApexChart'
import CDataTable from '@/components/common/CDataTable'
import MemoCell from '@/components/common/MemoCell'
import * as XLSX from 'xlsx'

export default {
  name: 'GrowthAdManager',
  components: { ApexChart, CDataTable, MemoCell },
  data() {
    return {
      loading: false,
      loadingText: '',
      showData: false,
      startDate: '',
      endDate: '',
      selectedCampaign: null,
      showCampaignModal: false,
      campaignChartReady: false,
      historyFilterCid: '',   // ③ 캠페인 필터 ('' = 전체)
      skuFilterCid: '',       // ② SKU 테이블 캠페인 필터 ('' = 전체)
      selectedSku: null,      // ② 상세 모달에 띄운 SKU
      showSkuModal: false,
      campaignMemos: {},      // String(campaignId) → 메모 (SalesLedgerMemo 재사용: scope=product, vendorItemId='campaign:'+id)
      appliedActions: [],     // 이번 세션에서 "적용함" 누른 액션 key (영속 기록은 캠페인 메모)
      campaignHeaders: [
        { text: '캠페인', value: 'name', isSlot: true },
        { text: '타입', value: 'campaignType', align: 'center', isSlot: true, width: 100 },
        { text: '상태', value: '_status', align: 'center', isSlot: true, width: 150 },
        { text: '성과', value: '_perf', align: 'right', isSlot: true, width: 150 },
        { text: '광고ROI', value: 'adRoi', align: 'right', isSlot: true, width: 90 },
        { text: '자연 견인', value: '_natural', align: 'center', isSlot: true, width: 90 },
        { text: '현재→권장 목표', value: '_rec', isSlot: true, width: 230 },
        { text: '권장 액션', value: '_actions', isSlot: true, width: 190 },
        { text: '예산(소진)', value: 'budget', align: 'right', isSlot: true, width: 110 },
        { text: '메모', value: '_memo', isSlot: true, width: 180 }
      ],
      campaignHeaderTooltips: {
        _perf: '기간 내 실측 — 광고비(부가세 포함 실부담) / 광고매출 / 전환수',
        adRoi: '광고로 번 마진 ÷ 광고비 · 100%가 본전, 넘으면 흑자',
        _natural: '광고+자연 전체 매출 ÷ 광고비. 오름세면 광고가 자연 노출·판매를 끌어올리고 있다는 신호 (참고치)',
        _rec: '현재 → 권장값. 권장값을 쿠팡 목표ROAS 입력칸에 그대로 넣으세요',
        _actions: '이 캠페인에 해당하는 권장 조치 — 칩을 누르면 상세가 열려요',
        budget: '일예산과 소진율 · 100%면 예산이 노출을 막는 중',
        _memo: '캠페인 작업 일지 — 할 일 "적용함"을 누르면 자동으로 기록돼요. 회사 단위 공유'
      },
      // ② SKU(상품)별 테이블
      skuHeaders: [
        { text: '상품', value: '_product', isSlot: true },
        { text: '캠페인', value: '_campaigns', isSlot: true, width: 160 },
        { text: '광고비', value: '_cost', align: 'right', isSlot: true, width: 110 },
        { text: '매출', value: '_sales', align: 'right', isSlot: true, width: 110 },
        { text: '전환', value: '_orders', align: 'right', isSlot: true, width: 80 },
        { text: '광고ROI', value: '_adRoi', align: 'right', isSlot: true, width: 90 },
        { text: '권장 (영역별)', value: '_strategy', isSlot: true, width: 240 },
        { text: '판매지속', value: '_continue', align: 'center', isSlot: true, width: 90 }
      ],
      skuHeaderTooltips: {
        _cost: '기간 내 광고비 — 부가세 포함 실부담',
        _sales: '광고로 발생한 매출 (할인 적용가 기준)',
        _adRoi: '광고로 번 마진 ÷ 광고비 · 100%가 본전, 넘으면 흑자',
        _strategy: '영역(검색·비검색·오디언스)별로 권장하는 광고전략 — 칩을 누르면 상세가 열려요',
        _continue: '현재 마진·전환 기준 판매를 계속할 만한지 (참고치)'
      },
      historyHeaders: [
        { text: '날짜', value: 'date', align: 'center', isSlot: true, width: 80 },
        { text: '변경 내용', value: '_changes', isSlot: true, width: 160 },
        { text: '광고비', value: 'cost', align: 'right', isSlot: true, width: 100 },
        { text: '광고매출', value: 'adSales', align: 'right', isSlot: true, width: 100 },
        { text: '자연매출', value: 'naturalSales', align: 'right', isSlot: true, width: 100 },
        { text: '전체ROAS', value: 'totalRoas', align: 'right', isSlot: true, width: 100 },
        { text: '광고ROAS', value: 'adRoas', align: 'right', isSlot: true, width: 100 },
        { text: '자연노출', value: 'exposure', align: 'right', isSlot: true, width: 100 }
      ],
      historyHeaderTooltips: {
        _changes: '그 날 바꾼 설정(목표ROAS·예산·CPC·ON/OFF 등)',
        cost: '부가세 포함 실부담 광고비',
        adSales: '광고 중인 상품의 광고 판매 매출',
        naturalSales: '광고 중인 상품의 광고 외(자연) 판매 매출 — 광고 안 하는 상품은 제외',
        totalRoas: '(광고매출 + 자연매출) ÷ 광고비 · 오름세면 광고가 자연 판매를 끌어올리는 신호',
        adRoas: '광고매출 ÷ 광고비',
        exposure: '노출추적에 등록된 광고 상품의 자연 노출수'
      },
      chartKey: 0,
      chartReady: false,
      rawData: { snapshots: [], adData: [], mappings: [], keywordData: [], changes: [], campaignHeadlines: [], diagnostics: [] }
    }
  },
  computed: {
    headlines() { return this.rawData.campaignHeadlines || [] },
    // 이 계정의 광고 데이터 존재 여부 — 없으면 수집 안내
    hasAnyAdData() {
      return (this.rawData.snapshots.length + this.rawData.adData.length + this.headlines.length) > 0
    },
    diagnostics() { return this.rawData.diagnostics || [] },
    // ── ② SKU(상품)별 성과 — 서버 계약 (미배포 서버면 undefined → 안내로 대체) ──
    skuPerformance() { return Array.isArray(this.rawData.skuPerformance) ? this.rawData.skuPerformance : null },
    hasSkuPerformance() { return !!this.skuPerformance },
    // 캠페인 필터 옵션 — SKU가 걸린 캠페인만
    skuFilterOptions() {
      var opts = [{ value: '', text: '전체 캠페인' }]
      var seen = {}
      for (var s of (this.skuPerformance || [])) {
        var ids = s.campaignIds || [], names = s.campaignNames || []
        for (var i = 0; i < ids.length; i++) {
          var cid = String(ids[i])
          if (seen[cid]) continue
          seen[cid] = true
          opts.push({ value: cid, text: names[i] || cid })
        }
      }
      return opts
    },
    // 필터 적용된 SKU 행
    skuRows() {
      var rows = (this.skuPerformance || [])
      if (this.skuFilterCid) {
        rows = rows.filter(s => (s.campaignIds || []).some(id => String(id) === String(this.skuFilterCid)))
      }
      // 광고비 큰 순 — 의미있는 상품을 위로 (서버는 삽입순이라 0원 SKU가 섞임)
      return rows.slice().sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0))
    },
    // 모달: 영역 3열 (데이터 없는 영역은 area=null로 둬서 '데이터 없음' 표기)
    skuAreaColumns() {
      var s = this.selectedSku
      var areas = (s && s.areas) || {}
      return [
        { key: 'search', label: '검색', area: this.hasAreaData(areas.search) ? areas.search : null },
        { key: 'notSearch', label: '비검색', area: this.hasAreaData(areas.notSearch) ? areas.notSearch : null },
        { key: 'audience', label: '오디언스', area: this.hasAreaData(areas.audience) ? areas.audience : null }
      ]
    },
    // 모달 매트릭스: 지표=행 (영역=열). adSourcing 49컬럼을 영역×지표로 압축
    skuAreaMetrics() {
      var self = this
      var pct = v => (v === null || v === undefined) ? '-' : (Math.round(v * 1000) / 10) + '%'
      var won = v => self.formatNum(v) + '원'
      return [
        { key: 'impressions', label: '노출', hint: '노출수', fmt: a => self.formatNum(a.impressions) },
        { key: 'clicks', label: '클릭', hint: '클릭수', fmt: a => self.formatNum(a.clicks) },
        { key: 'orders', label: '주문', hint: '주문수', fmt: a => self.formatNum(a.orders) },
        { key: 'units', label: '판매수량', hint: '판매수량', fmt: a => self.formatNum(a.units) },
        { key: 'sales', label: '매출', hint: '광고 매출', fmt: a => won(a.sales) },
        { key: 'ctr', label: '클릭률(CTR)', hint: '클릭 ÷ 노출', fmt: a => pct(a.ctr > 1 ? a.ctr / 100 : a.ctr) },
        { key: 'cvr', label: '전환율(CVR)', hint: '주문 ÷ 클릭', fmt: a => pct(a.cvr) },
        { key: 'm', label: '건당 마진', hint: '주문 1건당 통합마진', fmt: a => won(a.m) },
        { key: 'marginSum', label: '총수익', hint: '마진 합계', fmt: a => won(a.marginSum) },
        { key: 'cost', label: '광고비', hint: '부가세 포함 실부담', fmt: a => won((a.cost || 0) * 1.1) },
        { key: 'profit', label: '순수익', hint: '총수익 − 광고비', fmt: a => won(a.profit), cls: a => (a.profit >= 0 ? 'text-success' : 'text-danger') },
        { key: 'cpc', label: 'CPC 현재', hint: '현재 클릭당 비용', fmt: a => won(a.cpc) },
        { key: 'cpcUsl', label: 'CPC 한도', hint: '이 위로 올리면 적자', fmt: a => won(a.cpcUsl) },
        { key: 'cpcOpt', label: '최적 CPC', hint: '순이익이 가장 큰 CPC', fmt: a => won(a.cpcOpt), cls: () => 'font-weight-bold' },
        { key: 'minCvr', label: '최소 전환율', hint: '이 아래면 광고 적자', fmt: a => pct(a.minCvr) },
        { key: 'adRoi', label: '광고ROI', hint: '마진 ÷ 광고비 · 100%가 본전', fmt: a => (Math.round((a.adRoi || 0) * 100)) + '%', cls: a => self.adRoiColor(Math.round((a.adRoi || 0) * 100)) },
        { key: 'validated', label: '검증 완료', hint: '클릭 11+·전환 3+ 충족', fmt: a => a.validated ? '완료' : '참고', cls: a => a.validated ? 'text-success' : 'text-muted' },
        { key: 'strategy', label: '광고전략', hint: '이 영역에서 권장하는 방향', fmt: a => a.strategy || '-', cls: a => self.strategyTextClass(a.strategy) }
      ]
    },
    mappingByCampaign() {
      var m = {}
      for (var item of this.rawData.mappings) {
        if (!m[item.campaignId]) m[item.campaignId] = new Set()
        m[item.campaignId].add(item.vendorItemId)
      }
      return m
    },
    hasMappings() {
      return this.rawData.mappings.length > 0
    },
    // growthdb 조회 맵 — vendorItemId가 소스마다 number/string으로 달라 String 키로 통일 (이름·할인가 조인)
    gdbByVid() {
      var m = {}
      for (var g of (this.$store.state.growthdb || [])) {
        var k = String(g.vendorItemId)
        if (!m[k]) m[k] = g // 중복 시 첫 항목 유지 — 기존 .find() 동작과 동일
      }
      return m
    },
    // ── ③ 날짜별 변경 & 추이 — 서버 history 계약 (구버전 서버면 undefined → 안내로 대체) ──
    history() { return this.rawData.history || null },
    hasHistory() { return !!(this.history && Array.isArray(this.history.daily)) },
    historyFilterOptions() {
      var opts = [{ value: '', text: '전체 캠페인' }]
      for (var h of this.headlines) opts.push({ value: String(h.campaignId), text: h.name })
      return opts
    },
    // 일별 행 (최신순) — 광고비는 ×1.1 실부담, 전체ROAS=(광고+자연매출)/광고비
    historyRows() {
      if (!this.hasHistory) return []
      var src = this.historyFilterCid
        ? (((this.history.perCampaign || {})[this.historyFilterCid]) || [])
        : (this.history.daily || [])
      var changesByDate = {}
      for (var ch of (this.rawData.changes || [])) {
        if (this.historyFilterCid && String(ch.campaignId) !== String(this.historyFilterCid)) continue
        if (!changesByDate[ch.date]) changesByDate[ch.date] = []
        changesByDate[ch.date].push(ch)
      }
      return src.map(r => {
        var cost = (r.adCost || 0) * 1.1
        var adSales = r.adSales || 0
        var naturalSales = r.naturalSales || 0
        // 광고비가 미미한 날(1,000원 미만)의 ROAS는 분모가 작아 수십만%로 폭발 — 의미 없으니 null('-')
        var costOk = cost >= 1000
        return {
          date: r.date,
          changes: changesByDate[r.date] || [],
          cost: Math.round(cost),
          adSales: Math.round(adSales),
          naturalSales: Math.round(naturalSales),
          totalRoas: costOk ? Math.round((adSales + naturalSales) / cost * 100) : null,
          adRoas: costOk ? Math.round(adSales / cost * 100) : null,
          exposure: (r.exposure === null || r.exposure === undefined) ? null : r.exposure
        }
      }).sort((a, b) => String(b.date).localeCompare(String(a.date)))
    },
    historyHasExposure() {
      return this.historyRows.some(r => r.exposure !== null)
    },
    // 노출추적 안내 — 광고 상품 대비 추적 등록이 부족하면 (노출이 일부 보여도) 수집을 권함
    needExposureHint() {
      if (!this.hasHistory || !this.historyRows.length) return false
      var cov = this.history.exposureCoverage || {}
      return (cov.tracked || 0) < (cov.advertised || 0)
    },
    historyChartOptions() {
      var rows = [...this.historyRows].reverse() // 오름차순
      var categories = rows.map(r => this.formatDate(r.date))
      var hasExposure = this.historyHasExposure
      // ROAS 축 상한: 아웃라이어 하루가 축을 망가뜨려 선이 바닥에 깔리는 것 방지 (기존 클램프 기법)
      var roasVals = []
      for (var r0 of rows) { if (r0.adRoas > 0) roasVals.push(r0.adRoas); if (r0.totalRoas > 0) roasVals.push(r0.totalRoas) }
      roasVals.sort((a, b) => a - b)
      var roasP75 = roasVals.length ? roasVals[Math.floor((roasVals.length - 1) * 0.75)] : 0
      var roasMax = roasP75 > 0 ? Math.max(300, Math.ceil(roasP75 * 2 / 100) * 100) : undefined
      // 금액 시리즈는 한 축 스케일 공유 (보이는 축은 1개)
      var moneyMax = 0
      for (var r1 of rows) moneyMax = Math.max(moneyMax, r1.cost, r1.adSales, r1.naturalSales)
      moneyMax = moneyMax > 0 ? Math.ceil(moneyMax * 1.1) : undefined
      var annotations = []
      for (var row of rows) {
        for (var ch of row.changes) {
          annotations.push({
            x: this.formatDate(row.date),
            borderColor: '#FF4560',
            label: { text: this.formatChange(ch), borderColor: '#FF4560', style: { background: '#FF4560', color: '#fff', fontSize: '10px' }, orientation: 'horizontal', offsetY: -5 }
          })
        }
      }
      var series = [
        { name: '광고비', type: 'column', data: rows.map(r => r.cost) },
        { name: '광고매출', type: 'area', data: rows.map(r => r.adSales) },
        { name: '자연매출', type: 'area', data: rows.map(r => r.naturalSales) },
        { name: '광고ROAS', type: 'line', data: rows.map(r => r.adRoas) },
        { name: '전체ROAS', type: 'line', data: rows.map(r => r.totalRoas) }
      ]
      var colors = ['#adb5bd', '#4dabf7', '#51cf66', '#007bff', '#e8590c']
      var strokeW = [0, 2, 2, 3, 3]
      var dash = [0, 0, 0, 0, 0]
      var fillOp = [0.9, 0.25, 0.25, 1, 1]
      var moneyFmt = v => this.formatNum(v) + '원'
      var pctFmt = v => Math.round(v) + '%'
      var yaxis = [
        { min: 0, max: moneyMax, title: { text: '금액 (원)' }, labels: { formatter: v => this.formatNum(v) } },
        { min: 0, max: moneyMax, show: false },
        { min: 0, max: moneyMax, show: false },
        { opposite: true, min: 0, max: roasMax, title: { text: 'ROAS (%)' }, labels: { formatter: v => Math.round(v) + '%' } },
        { opposite: true, min: 0, max: roasMax, show: false }
      ]
      var tooltipY = [
        { formatter: moneyFmt }, { formatter: moneyFmt }, { formatter: moneyFmt },
        { formatter: pctFmt }, { formatter: pctFmt }
      ]
      if (hasExposure) {
        series.push({ name: '자연 노출수', type: 'line', data: rows.map(r => r.exposure === null ? 0 : r.exposure) })
        colors.push('#868e96'); strokeW.push(2); dash.push(5); fillOp.push(1)
        yaxis.push({ min: 0, show: false })
        tooltipY.push({ formatter: v => this.formatNum(v) + '회' })
      }
      return {
        chart: { type: 'line', height: 340, toolbar: { show: true } },
        series,
        xaxis: { categories },
        yaxis,
        annotations: { xaxis: annotations },
        colors,
        stroke: { width: strokeW, dashArray: dash, curve: 'straight' },
        fill: { opacity: fillOp },
        dataLabels: { enabled: false },
        plotOptions: { bar: { columnWidth: '55%' } },
        tooltip: { shared: true, intersect: false, y: tooltipY },
        legend: { position: 'top' }
      }
    },
    // ① '자연 견인' 컬럼 — 기간 전반부 vs 후반부 전체ROAS 비교 (perCampaign 기반, 없으면 '-')
    naturalPullByCid() {
      var out = {}
      if (!this.history || !this.history.perCampaign) return out
      var ratio = arr => {
        var c = 0, s = 0
        for (var r of arr) { c += (r.adCost || 0) * 1.1; s += (r.adSales || 0) + (r.naturalSales || 0) }
        return c > 0 ? s / c * 100 : 0
      }
      for (var cid in this.history.perCampaign) {
        var rows = (this.history.perCampaign[cid] || []).slice().sort((a, b) => String(a.date).localeCompare(String(b.date)))
        // 광고비가 미미한 날(1,000원 미만)은 비율이 폭발해 추세를 오염 — 유의미한 집행일만으로 판단
        var valid = rows.filter(r => (r.adCost || 0) * 1.1 >= 1000)
        if (valid.length < 4) continue
        var half = Math.floor(valid.length / 2)
        var first = ratio(valid.slice(0, half))
        var second = ratio(valid.slice(half))
        if (!(first > 0)) continue
        var arrow = second >= first * 1.1 ? '↑' : second <= first * 0.9 ? '↓' : '→'
        out[String(cid)] = { arrow, pct: Math.round(second) }
      }
      return out
    },
    // ① '권장 액션' 칩 — actionQueue를 캠페인별 칩 데이터로 축약 (문장 렌더는 모달이 담당)
    actionChipsByCid() {
      var out = {}
      for (var a of this.actionQueue) {
        if (!a.campaignId) continue
        var text = this.actionChip(a)
        if (!text) continue
        var cid = String(a.campaignId)
        if (!out[cid]) out[cid] = []
        out[cid].push({ key: a.key, severity: a.severity, text, title: a.action || a.reason || '' })
      }
      return out
    },
    // 모달에 띄운 캠페인의 액션만
    campaignActions() {
      if (!this.selectedCampaign) return []
      var cid = String(this.selectedCampaign.campaignId)
      return this.actionQueue.filter(a => a.campaignId && String(a.campaignId) === cid)
    },
    modalSpectrum() {
      return this.selectedCampaign ? (this.spectrumByCid[String(this.selectedCampaign.campaignId)] || null) : null
    },
    // 모달 일별 추이(구버전 fallback) — 이 캠페인에 매핑된 상품의 일별 광고비/매출
    campaignDailyRows() {
      if (!this.selectedCampaign) return []
      var items = this.getAdDataForCampaigns([this.selectedCampaign.campaignId])
      var byDate = {}
      for (var ad of items) {
        if (!byDate[ad.date]) byDate[ad.date] = []
        byDate[ad.date].push(ad)
      }
      return Object.keys(byDate).sort().map(d => {
        var agg = this.aggregateArea(byDate[d])
        return { date: d, cost: Math.round(agg.cost), sales: Math.round(agg.sales) }
      })
    },
    // 모달 차트 행 — history.perCampaign이 있으면 자연매출 포함, 없으면 기존 광고 데이터로
    campaignModalRows() {
      if (!this.selectedCampaign) return []
      var cid = String(this.selectedCampaign.campaignId)
      var per = (this.history && this.history.perCampaign) ? this.history.perCampaign[cid] : null
      if (per && per.length) {
        return per.slice().sort((a, b) => String(a.date).localeCompare(String(b.date))).map(r => ({
          date: r.date,
          cost: Math.round((r.adCost || 0) * 1.1),
          sales: Math.round(r.adSales || 0),
          natural: Math.round(r.naturalSales || 0)
        }))
      }
      return this.campaignDailyRows.map(r => ({ date: r.date, cost: r.cost, sales: r.sales, natural: null }))
    },
    campaignModalHasNatural() {
      return this.campaignModalRows.some(r => r.natural !== null)
    },
    campaignModalChartOptions() {
      var rows = this.campaignModalRows
      var series = [
        { name: '광고비', type: 'column', data: rows.map(r => r.cost) },
        { name: '광고매출', type: 'line', data: rows.map(r => r.sales) }
      ]
      var colors = ['#adb5bd', '#007bff']
      var strokeW = [0, 3]
      if (this.campaignModalHasNatural) {
        series.push({ name: '자연매출', type: 'line', data: rows.map(r => r.natural || 0) })
        colors.push('#28a745')
        strokeW.push(3)
      }
      return {
        chart: { type: 'line', height: 220, toolbar: { show: false } },
        series,
        xaxis: { categories: rows.map(r => this.formatDate(r.date)) },
        yaxis: { labels: { formatter: v => this.formatNum(v) } },
        colors,
        stroke: { width: strokeW },
        plotOptions: { bar: { columnWidth: '50%' } },
        tooltip: { shared: true, intersect: false, y: { formatter: v => this.formatNum(v) + '원' } },
        legend: { position: 'top' }
      }
    },
    // §15 게이지 캐시 (캠페인별 스펙트럼) — 템플릿 반복호출 방지
    spectrumByCid() {
      var m = {}
      for (var h of this.headlines) m[String(h.campaignId)] = this.roasSpectrum(h)
      return m
    },
    // 캠페인별 제외 후보 (타입별 손잡이 §1·§12). 제외상품=적자(ROI<100%)+광고비 의미있음 / 제외키워드=클릭20+ 전환0
    excludeByCampaign() {
      var out = {}
      for (var h of this.headlines) {
        var prods = (h.products || [])
          .filter(p => (p.cost * 1.1) > 1000 && p.adRoi < 1)
          .sort((a, b) => a.adRoi - b.adRoi).slice(0, 5)
        out[String(h.campaignId)] = { products: prods, keywords: [] }
      }
      var kwByCid = {}
      for (var kd of this.rawData.keywordData) {
        var cid = String(kd.campaignId)
        if (!kwByCid[cid]) kwByCid[cid] = {}
        if (!kwByCid[cid][kd.keyword]) kwByCid[cid][kd.keyword] = { keyword: kd.keyword, clicks: 0, cost: 0, orders: 0 }
        kwByCid[cid][kd.keyword].clicks += kd.clicks || 0
        kwByCid[cid][kd.keyword].cost += kd.cost || 0
        kwByCid[cid][kd.keyword].orders += kd.orders || 0
      }
      for (var c in kwByCid) {
        if (!out[c]) out[c] = { products: [], keywords: [] }
        out[c].keywords = Object.values(kwByCid[c])
          .filter(k => k.clicks >= 20 && k.orders === 0)
          .sort((a, b) => b.cost - a.cost).slice(0, 5)
      }
      return out
    },
    // 권장 액션 소스 — 진단 + 헤드라인 파생 액션을 우선순위로 (칩·모달 "할 일" 공용)
    actionQueue() {
      var actions = []
      var diagRank = { '죽은캠페인': 1, '자기잠식': 2, '예산잠식': 3, '충전가속': 4, '학습정체': 6 }
      var headByCid = {}
      for (var h0 of this.headlines) headByCid[String(h0.campaignId)] = h0
      var deadCids = new Set()  // 송출 불가(죽은캠페인) → 미세조정 액션 억제
      // 1) 운영 진단 (cross-campaign §24)
      for (var d of this.diagnostics) {
        var dh = d.campaignId ? headByCid[String(d.campaignId)] : null
        // OFF 캠페인 진단은 제외 (이미 꺼져 지금 할 일 아님). 상품단위 진단(campaignId 없음)은 유지.
        if (dh && dh.isActive === false) continue
        if (d.type === '죽은캠페인' && d.campaignId) deadCids.add(String(d.campaignId))
        // §24-1 재배치 액션 — before/after 숫자 근거(상품ROI vs 캠페인ROI, 광고비 점유)
        var relocation = null
        if (d.type === '예산잠식' && dh && d.vendorItemId) {
          var rp = (dh.products || []).find(p => String(p.vendorItemId) === String(d.vendorItemId))
          if (rp && dh.totalCost > 0) {
            relocation = {
              productName: this.productName(d.vendorItemId),
              fromType: dh.campaignType, fromName: dh.name,
              productRoi: Math.round((rp.adRoi || 0) * 100),
              campaignRoi: this.adRoiPct(dh),
              costShare: Math.round(rp.cost / dh.totalCost * 100)
            }
          }
        }
        actions.push({
          key: 'diag-' + (d.campaignId || '') + '-' + d.type + '-' + (d.vendorItemId || ''),
          priority: diagRank[d.type] || 5,
          severity: d.severity || 'mid',
          badge: d.type === '충전가속' ? '전환 쌓기' : d.type,
          campaignName: d.campaignName || (d.vendorItemId ? this.productName(d.vendorItemId) : ''),
          campaignId: d.campaignId || null,
          title: this.diagTitle(d),
          reason: relocation
            ? '효율 좋은 ' + relocation.productName + '이(가) 이 AI 광고비의 ' + relocation.costShare + '%만 받고 있어요. 상품 ROI ' + relocation.productRoi + '%로 캠페인 평균(' + relocation.campaignRoi + '%)보다 훨씬 높아, 전용 예산을 주면 더 팔립니다.'
            : d.message,
          action: this.diagAction(d),
          relocation,
          detail: this.diagDetail(d)
        })
      }
      // 2) 헤드라인 파생 액션 (ON 캠페인) — 기타/신규고객확보는 권장 산식 대상 아님(별개 손잡이)
      for (var h of this.headlines) {
        var cid = String(h.campaignId)
        if (h.campaignType === '기타' || h.campaignType === '신규고객확보') continue
        // 매출스타트(§27.2): 수익률 목표가 없는 충전용 타입 — 전환이 쌓이면 매출최적화 전환 검토 제안
        if (h.campaignType === '매출스타트') {
          if (h.isActive && (h.conv30 || 0) >= 30) {
            actions.push({
              key: 'graduate-' + cid, priority: 4, severity: 'low', badge: '전환 완료',
              campaignName: h.name, campaignId: h.campaignId,
              title: h.name + ' — 매출최적화로 전환 검토',
              reason: '최근 30일 전환이 ' + h.conv30 + '건으로 노출 활성화 목표(30건)를 채웠어요. 매출스타트는 수익률을 보지 않아 계속 두면 광고비가 공격적으로 나갑니다.',
              action: '광고센터에서 매출최적화(목표 수익률) 캠페인으로 전환',
              detail: '매출스타트는 초기 노출·전환을 빠르게 쌓는 용도예요. 목표를 달성했으니 이제 목표 수익률을 정해 이익을 관리하는 매출최적화로 갈아타는 걸 권장해요.'
            })
          }
          // 예산소진+흑자 → 예산 증액은 아래 공통 로직이 처리. ROAS/CPC/제외 권장은 해당 없음.
        }
        if (!h.isActive) {
          // 흑자였는데 꺼진 캠페인 — OFF는 진단·액션에서 빠지는 사각지대라 재가동 판단만 제안
          if (h.adRoi >= 1 && (h.totalOrders || 0) >= 3 && !this.isManualType(h.campaignType) && this.recInputPct(h) > 0) {
            actions.push({
              key: 'revive-' + cid, priority: 7, severity: 'low', badge: '재가동 검토',
              campaignName: h.name, campaignId: h.campaignId,
              title: h.name + ' — 다시 켤지 검토',
              reason: '꺼져 있지만 끄기 전 광고ROI ' + this.adRoiPct(h) + '%로 흑자였어요.',
              action: '켠다면 목표ROAS ' + this.recInputPct(h) + '%로 시작',
              detail: '성과가 나쁘지 않았던 캠페인이에요. 다시 켤 계획이면 권장 목표ROAS로 시작해 결과를 보고 조정하세요. 의도적으로 꺼둔 거라면 무시해도 됩니다.'
            })
          }
          continue
        }
        if (deadCids.has(cid)) continue  // 송출 불가 캠페인은 미세조정 무의미 — '끄거나 점검' 액션만 남김
        // 예산소진 + 흑자 → 예산 증액
        if (h.budgetUtilRate >= 100 && h.adRoi > 1) {
          actions.push({
            key: 'budget-' + cid, priority: 3, severity: 'mid', badge: '예산',
            campaignName: h.name, campaignId: h.campaignId,
            title: h.name + ' — 일예산 늘리기',
            reason: '예산을 100% 다 쓰는데 흑자예요(광고ROI ' + this.adRoiPct(h) + '%). 예산이 더 팔릴 기회를 막고 있어요.',
            action: '일예산 ' + this.formatNum(h.budget) + '원 → ' + this.formatNum(Math.round(h.budget * 1.3 / 1000) * 1000) + '원 (+30% 시작점)',
            detail: '예산 소진율이 100%면 광고가 하루 중 일찍 멈춰요. 흑자일 때는 예산을 늘릴수록 마진이 더 쌓입니다. 20~50% 올려보고 소진율·마진을 다시 확인하세요.'
          })
        }
        // 목표ROAS 조정 (전환 충분 + 권장과 차이 큼, AI/매최만)
        if (h.totalOrders >= 3 && h.recommendedRoasInput > 0 && h.currentRoasTarget > 0 && !this.isManualType(h.campaignType)) {
          var rec = this.recInputPct(h), cur = h.currentRoasTarget
          if (Math.abs(rec - cur) >= Math.max(50, cur * 0.15)) {
            var up = rec > cur
            actions.push({
              key: 'roas-' + cid, priority: up ? 4 : 5, severity: 'low', badge: 'ROAS',
              campaignName: h.name, campaignId: h.campaignId,
              title: h.name + ' — 목표ROAS ' + (up ? '올리기' : '내리기'),
              reason: up
                ? '지금 목표ROAS가 권장보다 낮아 광고비가 과해요. 올리면 흑자폭이 커집니다.'
                : '지금 목표ROAS가 권장보다 높아 노출이 적어요. 내리면 판매가 늘어납니다.',
              action: '목표ROAS ' + cur + '% → ' + rec + '%',
              detail: '권장값은 현재 마진율·전환율로 계산한 "순이익이 가장 커지는 지점"이에요. 쿠팡 입력칸에는 ' + rec + '%를 넣으세요(쿠팡 ROAS는 부가세 때문에 실제보다 높게 표시돼 그만큼 반영한 값입니다).'
            })
          }
        }
        // 원가입력 필요 (매출스타트는 권장 산식을 안 쓰므로 제외)
        if (h.marginEstimated && h.campaignType !== '매출스타트') {
          actions.push({
            key: 'cost-' + cid, priority: 8, severity: 'low', badge: '원가',
            campaignName: h.name, campaignId: h.campaignId,
            title: h.name + ' — 매입가 입력 필요',
            reason: '매입 원가가 없어 마진을 기본값으로 추정 중이라 추천 ROAS가 부정확해요.',
            action: '그로스DB > 매입정보에서 매입가 입력',
            detail: '매입가가 없으면 마진을 기본값(5,000원)으로 가정해 계산해요. 실제 매입가를 넣으면 추천 목표ROAS가 정확해집니다.'
          })
        }
        // ── 타입별 손잡이 액션 (§1·§12) ──
        var isManual = this.isManualType(h.campaignType)
        var ex = this.excludeByCampaign[cid] || { products: [], keywords: [] }
        // 수동: 검색 CPC (수동은 ROAS가 아니라 CPC가 손잡이)
        if (isManual && !this.isLowData(h)) {
          actions.push({
            key: 'cpc-' + cid, priority: 4, severity: 'low', badge: 'CPC',
            campaignName: h.name, campaignId: h.campaignId,
            title: h.name + ' — 검색 CPC 맞추기',
            reason: '수동광고는 목표ROAS가 아니라 CPC로 조절해요. 지금 전환율 기준 순이익이 가장 커지는 CPC는 ' + this.recCpc(h) + '원이에요.',
            action: (h.campaignType === '수동스마트타겟팅' ? '일괄 CPC' : '검색 CPC') + ' → ' + this.recCpc(h) + '원',
            detail: '검색 CPC를 이 값으로 시작해 노출·전환을 보고 다시 조정하세요. 너무 낮으면 노출이 안 나오고, ' + (h.m ? Math.round(h.m * h.cvr) : '한도') + '원을 넘으면 적자예요(시작값일 뿐, 결과 보고 대응).'
          })
        }
        // AI/매최: 제외상품(적자) — AI=제외상품 추가 / 매최=광고상품에서 빼기 (매출스타트는 제외 손잡이 없음 §27.2)
        if ((h.campaignType === 'AI광고' || h.campaignType === '매출최적화') && ex.products.length) {
          var ep = ex.products[0]
          actions.push({
            key: 'exprod-' + cid, priority: 6, severity: 'low', badge: '제외상품',
            campaignName: h.name, campaignId: h.campaignId,
            title: h.name + ' — ' + (h.campaignType === 'AI광고' ? '적자 상품 제외하기' : '적자 상품 빼기'),
            reason: '광고비를 1,000원 넘게 쓰는데 적자인 상품이 ' + ex.products.length + '개 있어요(가장 심한 건 ' + this.productName(ep.vendorItemId) + ' ROI ' + Math.round(ep.adRoi * 100) + '%). 빼면 그 예산이 잘 되는 상품으로 갑니다.',
            action: (h.campaignType === 'AI광고' ? '제외상품에 추가' : '광고상품에서 제외') + ' (' + ex.products.length + '개)',
            detail: ex.products.map(p => '· ' + this.productName(p.vendorItemId) + ' — 광고비 ' + this.formatNum(p.cost * 1.1) + '원, ROI ' + Math.round(p.adRoi * 100) + '%(적자)').join('\n')
          })
        }
        // 제외키워드 — AI·매최·수동스마트타겟팅 공통 손잡이(§12). 수동키워드는 '키워드 삭제', 매출스타트는 해당 없음
        if (ex.keywords.length && (h.campaignType !== '수동키워드') && (h.campaignType !== '매출스타트')) {
          actions.push({
            key: 'exkw-' + cid, priority: 6, severity: 'low', badge: '제외키워드',
            campaignName: h.name, campaignId: h.campaignId,
            title: h.name + ' — 돈만 쓰는 키워드 제외하기',
            reason: '클릭 20회 넘게 받고도 전환 0인 키워드가 ' + ex.keywords.length + '개 있어요. 제외하면 헛돈을 막습니다.',
            action: '제외키워드에 추가 (' + ex.keywords.length + '개)',
            detail: ex.keywords.map(k => '· "' + k.keyword + '" — 클릭 ' + k.clicks + ', 광고비 ' + this.formatNum(k.cost) + '원, 전환 0').join('\n')
          })
        }
        if (ex.keywords.length && h.campaignType === '수동키워드') {
          actions.push({
            key: 'delkw-' + cid, priority: 6, severity: 'low', badge: '키워드삭제',
            campaignName: h.name, campaignId: h.campaignId,
            title: h.name + ' — 안 되는 키워드 삭제하기',
            reason: '클릭 20회 넘게 받고도 전환 0인 키워드가 ' + ex.keywords.length + '개 있어요. 직접 추가한 키워드라 삭제하면 됩니다.',
            action: '키워드 삭제 (' + ex.keywords.length + '개)',
            detail: ex.keywords.map(k => '· "' + k.keyword + '" — 클릭 ' + k.clicks + ', 광고비 ' + this.formatNum(k.cost) + '원, 전환 0').join('\n')
          })
        }
      }
      return actions.sort((a, b) => a.priority - b.priority)
    }
  },
  watch: {
    historyFilterCid() { this.refreshChart() }
  },
  async mounted() {
    var today = new Date()
    var ago = new Date(today)
    ago.setDate(today.getDate() - 30)
    this.endDate = today.toISOString().split('T')[0]
    this.startDate = ago.toISOString().split('T')[0]
    try {
      if (!this.$store.state.growthdb || this.$store.state.growthdb.length === 0) {
        var r = await axios.post('/api/getGrowthDBV2')
        if (r.data && r.data.growthdb) this.$store.commit('growthdbupdate', r.data.growthdb)
      }
    } catch (e) {
      console.error('growthDB 로드 실패', e) // eslint-disable-line
    }
  },
  methods: {
    async search() {
      if (!this.startDate || !this.endDate) return alert('조회 기간을 선택해주세요.')
      this.loading = true
      this.loadingText = '광고 대시보드 데이터 조회 중...'
      try {
        var sd = this.startDate.replace(/-/g, '')
        var ed = this.endDate.replace(/-/g, '')
        var res = await axios.post('/api/growth/getAdDashboardData', { startDate: sd, endDate: ed })
        if (res.data.success) {
          this.rawData = res.data.data
          console.log('[광고관리] snapshots:', this.rawData.snapshots.length, 'adData:', this.rawData.adData.length, 'mappings:', this.rawData.mappings.length, 'keywords:', this.rawData.keywordData.length, 'changes:', this.rawData.changes.length, 'history:', this.rawData.history ? (this.rawData.history.daily || []).length + '일' : '없음(구버전 서버)') // eslint-disable-line
          this.showData = true
          this.historyFilterCid = ''
          this.loadCampaignMemos()
          this.$nextTick(() => { this.chartReady = true; this.refreshChart() })
        } else {
          alert(res.data.message || '조회 실패')
        }
      } catch (e) {
        console.error('대시보드 조회 오류', e) // eslint-disable-line
        alert('데이터 조회 중 오류가 발생했습니다.')
      } finally {
        this.loading = false
      }
    },
    refreshChart() {
      this.chartReady = false
      this.$nextTick(() => { this.chartKey++; this.chartReady = true })
    },
    getAdDataForCampaigns(campaignIds) {
      if (!campaignIds || campaignIds.length === 0) return []
      var vids = new Set()
      for (var cid of campaignIds) {
        var mapped = this.mappingByCampaign[cid]
        if (mapped) mapped.forEach(v => vids.add(v))
      }
      if (vids.size > 0) return this.rawData.adData.filter(a => vids.has(a.vendorItemId))
      if (!this.hasMappings) return this.rawData.adData
      return []
    },
    discUnitPrice(vid) {
      var g = this.gdbByVid[String(vid)]
      return g ? ((g.salePrice || 0) - (g.coupon || 0)) : 0  // 할인가 = 판매가 − 쿠폰
    },
    aggregateArea(items) {
      var r = { cost: 0, clicks: 0, impressions: 0, sales: 0, orders: 0, units: 0, profit: 0 }
      for (var a of items) {
        r.cost += (a.cost || 0) * 1.1; r.clicks += a.clicks || 0; r.impressions += a.impressions || 0 // 실부담(VAT 포함)
        if (a.detail) { for (var d of a.detail) { var p = this.discUnitPrice(d.vendorItemId); r.sales += (p > 0) ? p * (d.units || 0) : (d.sales || 0); r.orders += d.orders || 0; r.units += d.units || 0 } } // 할인가 매출
      }
      r.profit = r.sales - r.cost
      r.ctr = r.impressions > 0 ? ((r.clicks / r.impressions) * 100).toFixed(2) : '0.00'
      return r
    },
    adRoiPct(h) { return Math.round((h.adRoi || 0) * 100) },
    // 광고ROI 색: 100%=본전. 흑자 초록/파랑, 적자 빨강
    adRoiColor(pct) {
      if (pct >= 120) return 'text-success font-weight-bold'
      if (pct >= 100) return 'text-primary'
      return 'text-danger'
    },
    // 검증 데이터 충분 여부 (§4: 클릭 11+ / 전환 3+)
    isLowData(h) { return (h.totalOrders || 0) < 3 || (h.totalClicks || 0) < 11 },
    // 판단보류 상태 (§23 D1): 송출불가·학습정체면 ROAS/예산 미세조정보다 상태 해결이 먼저
    isBlocked(h) {
      var sv = String(h.servingStatus || '')
      return sv.includes('ABORTED') || sv.includes('ERROR') || h.learningStatus === 'LEARNING_STOPPED'
    },
    actionBadgeClass(sev) {
      if (sev === 'high') return 'badge-danger'
      if (sev === 'mid') return 'badge-warning'
      return 'badge-info'
    },
    // 액션 → ① 권장 액션 칩 라벨. ROAS/CPC/원가는 '현재→권장' 칸이, 학습정체는 상태 칩이 담당하므로 제외
    actionChip(a) {
      var k = a.key || ''
      if (k.indexOf('budget-') === 0) return '예산↑'
      if (k.indexOf('exprod-') === 0) return '제외상품 ' + this.excludeCount(a.campaignId, 'products')
      if (k.indexOf('exkw-') === 0 || k.indexOf('delkw-') === 0) return '제외키워드 ' + this.excludeCount(a.campaignId, 'keywords')
      if (k.indexOf('revive-') === 0) return '재가동 검토'
      if (k.indexOf('diag-') === 0) {
        if (a.badge === '죽은캠페인') return '끄기/점검'
        if (a.badge === '전환 쌓기') return '전환 쌓기'
        if (a.badge === '예산잠식') return a.relocation ? '분리: ' + this.shortName(a.relocation.productName) : '분리 검토'
        if (a.badge === '자기잠식') return '중복 정리'
        return null
      }
      return null
    },
    excludeCount(cid, kind) {
      var ex = this.excludeByCampaign[String(cid)]
      return (ex && ex[kind]) ? ex[kind].length : 0
    },
    shortName(name) {
      var s = String(name || '')
      return s.length > 8 ? s.slice(0, 8) + '…' : s
    },
    naturalPullClass(np) {
      if (np.arrow === '↑') return 'text-success font-weight-bold'
      if (np.arrow === '↓') return 'text-danger'
      return 'text-muted'
    },
    openCampaignModal(h) {
      this.selectedCampaign = h
      this.showCampaignModal = true
    },
    // ── ② SKU(상품)별 테이블 헬퍼 ──
    openSkuModal(s) {
      this.selectedSku = s
      this.showSkuModal = true
    },
    // 노출 0(또는 영역 객체 없음)이면 데이터 없는 영역 — 칩·열에서 생략
    hasAreaData(a) { return !!(a && (a.impressions || 0) > 0) },
    // 캠페인 라벨 — 1개면 이름, 여러개면 'OO 외 N'
    skuCampaignLabel(s) {
      var names = s.campaignNames || []
      if (!names.length) return '-'
      if (names.length === 1) return names[0]
      return names[0] + ' 외 ' + (names.length - 1)
    },
    // 첫 화면 권장 칩 — 노출 있는 영역만, '영역 전략' 형식
    skuStrategyChips(s) {
      var areas = (s && s.areas) || {}
      var label = { search: '검색', notSearch: '비검색', audience: '오디언스' }
      var verb = { '광고최적화': '최적화', '광고유지': '유지', '광고최소화': '최소화', '광고중지': '중지' }
      var out = []
      for (var key of ['search', 'notSearch', 'audience']) {
        var a = areas[key]
        if (!this.hasAreaData(a) || !a.strategy) continue
        out.push({ area: key, strategy: a.strategy, text: label[key] + ' ' + (verb[a.strategy] || a.strategy) })
      }
      return out
    },
    // 전략별 배지 색 (최적화=초록, 유지=파랑, 최소화=노랑, 중지=빨강)
    strategyClass(strategy) {
      if (strategy === '광고최적화') return 'badge-success'
      if (strategy === '광고유지') return 'badge-primary'
      if (strategy === '광고최소화') return 'badge-warning'
      if (strategy === '광고중지') return 'badge-danger'
      return 'badge-secondary'
    },
    strategyTextClass(strategy) {
      if (strategy === '광고최적화') return 'text-success font-weight-bold'
      if (strategy === '광고유지') return 'text-primary'
      if (strategy === '광고최소화') return 'text-warning'
      if (strategy === '광고중지') return 'text-danger'
      return 'text-muted'
    },
    // 권장 액션 칩 → 캠페인 상세 모달 연결
    goToCampaign(cid) {
      if (!cid) return
      var h = this.headlines.find(x => String(x.campaignId) === String(cid))
      if (h) this.openCampaignModal(h)
    },
    // 모달 안에서는 캠페인명 접두사 없이 액션만 표시
    actionVerb(a) {
      var prefix = (a.campaignName || '') + ' — '
      return (a.campaignName && a.title.indexOf(prefix) === 0) ? a.title.slice(prefix.length) : a.title
    },
    // §18 상품별 양방향 판단 — 분리(예산잠식 진단 재사용) / 제외(적자, excludeByCampaign과 같은 기준) / 유지
    productVerdict(p, h) {
      var isReloc = this.diagnostics.some(d => d.type === '예산잠식' && String(d.campaignId) === String(h.campaignId) && String(d.vendorItemId) === String(p.vendorItemId))
      if (isReloc) return { text: '분리 후보 — 전용 매출최적화로', cls: 'text-primary font-weight-bold' }
      var orders = p.orders || 0
      if ((p.cost * 1.1) > 1000 && p.adRoi < 1) {
        // 전환 0인데 광고비만 쓴 상품 = 적자보다 더 명확한 제외 사유 — 라벨로 구분
        return { text: orders === 0 ? '제외 후보 — 전환 0' : '제외 후보 — 적자', cls: 'text-danger font-weight-bold' }
      }
      if (orders === 0) return { text: '보류 — 전환 0', cls: 'text-muted' }
      return { text: '유지', cls: 'text-muted' }
    },
    // ── 캠페인 메모 — SalesLedgerMemo 재사용 (scope=product, vendorItemId='campaign:'+id — 서버 무변경. 실제 상품ID는 숫자만이라 충돌 없음) ──
    async loadCampaignMemos() {
      try {
        var res = await axios.post('/api/growth/getSalesLedgerMemos')
        if (res.data && res.data.res === 'success') {
          var m = {}
          for (var memo of (res.data.memos || [])) {
            var vid = String(memo.vendorItemId || '')
            if (memo.scope === 'product' && vid.indexOf('campaign:') === 0) m[vid.slice(9)] = memo.content || ''
          }
          this.campaignMemos = m
        }
      } catch (e) {
        console.error('캠페인 메모 로드 실패', e) // eslint-disable-line
      }
    },
    getCampaignMemo(h) {
      return this.campaignMemos[String(h.campaignId)] || ''
    },
    async saveCampaignMemo(h, content) {
      var cid = String(h.campaignId)
      try {
        var res = await axios.post('/api/growth/saveSalesLedgerMemo', {
          scope: 'product', date: null, vendorItemId: 'campaign:' + cid, content
        })
        if (!res.data || res.data.res !== 'success') {
          console.error('캠페인 메모 저장 실패', res.data) // eslint-disable-line
          return
        }
        var trimmed = (content || '').trim()
        if (trimmed) this.$set(this.campaignMemos, cid, trimmed)
        else this.$delete(this.campaignMemos, cid)
      } catch (e) {
        console.error('캠페인 메모 저장 오류', e) // eslint-disable-line
      }
    },
    isApplied(a) { return this.appliedActions.includes(a.key) },
    // "적용함" — 캠페인 메모에 날짜+액션 자동 기록 (대응 이력의 '왜'를 남김)
    async markActionApplied(a) {
      if (!a.campaignId) return
      var h = this.headlines.find(x => String(x.campaignId) === String(a.campaignId))
      if (!h) return
      var now = new Date()
      var line = (now.getMonth() + 1) + '/' + now.getDate() + ' 적용: ' + a.action
      var prev = this.getCampaignMemo(h)
      await this.saveCampaignMemo(h, prev ? prev + '\n' + line : line)
      if (!this.appliedActions.includes(a.key)) this.appliedActions.push(a.key)
    },
    // 진단 액션 문구 — 충전가속은 손익분기 구체값까지 (요약 계층이 실행에 필요한 숫자를 숨기지 않게)
    diagAction(d) {
      if (d.type === '충전가속' && d.campaignId) {
        var sp = this.spectrumByCid[String(d.campaignId)]
        if (sp) return '목표ROAS → ' + sp.brk + '% (손익분기)까지 낮춰 전환 모으기'
      }
      return d.suggestion
    },
    diagTitle(d) {
      var n = d.campaignName || (d.vendorItemId ? this.productName(d.vendorItemId) : '캠페인')
      var m = {
        '죽은캠페인': n + ' — 끄거나 점검하기',
        '자기잠식': n + ' — 중복 노출 정리하기',
        '예산잠식': n + ' — 효율 좋은 상품 분리하기',
        '충전가속': n + ' — 목표ROAS 낮춰 전환 쌓기',
        '학습정체': n + ' — 학습 멈춤 점검하기'
      }
      return m[d.type] || (n + ' — ' + d.type)
    },
    diagDetail(d) {
      var m = {
        '죽은캠페인': '캠페인이 켜져 있는데 쿠팡에서 광고가 나가지 않는 상태예요. 보통 품절·이미지 심사 실패·계정 문제 때문입니다. 원인을 고치거나, 안 쓸 거면 꺼서 혼선을 줄이세요.',
        '자기잠식': '같은 상품이 여러 광고에 동시에 걸려 서로 입찰 경쟁(내 돈으로 내 광고를 이기는 셈)을 하고 있어요. 한 캠페인만 남기고 나머지에서는 빼는 게 좋습니다.',
        '예산잠식': 'AI 광고 안에서 효율 좋은 상품이 광고비를 충분히 못 받고 있어요. 이 상품만 따로 매출최적화 캠페인으로 분리해 전용 예산을 주면 더 잘 팔립니다.',
        '충전가속': '최근 30일 전환이 30건 미만이라 쿠팡 광고 품질점수가 낮아요. AI광고는 노출 우대를 받으니, 목표ROAS를 손익분기까지 잠깐 낮춰 전환을 빠르게 30건까지 채우면 이후 효율이 올라갑니다.',
        '학습정체': '쿠팡 광고 학습이 멈춘 상태예요. 설정을 자주 바꿨거나 데이터가 부족하면 생깁니다. 한동안 설정을 그대로 두고 데이터가 쌓이길 기다려 보세요.'
      }
      return m[d.type] || d.suggestion || ''
    },
    recInputPct(h) { return Math.round((h.recommendedRoasInput || 0) * 100) },
    recArrow(h) {
      var cur = h.currentRoasTarget || 0, rec = this.recInputPct(h)
      if (!cur || !rec) return ''
      if (rec > cur * 1.1) return '↑'
      if (rec < cur * 0.9) return '↓'
      return '≈'
    },
    recArrowClass(h) {
      var cur = h.currentRoasTarget || 0, rec = this.recInputPct(h)
      if (!cur || !rec) return ''
      return (Math.abs(rec - cur) > cur * 0.1) ? 'text-primary font-weight-bold' : 'text-muted'
    },
    recCpc(h) {
      var c = (h.recommendedRoasTarget > 0) ? (h.S * h.cvr / h.recommendedRoasTarget) : 0
      c = Math.floor(c / 10) * 10
      return c < 100 ? 100 : c
    },
    // §15 ROAS/CPC 스펙트럼 게이지 — 손익분기──최적──최대 축에 현재·권장 위치.
    // 쿠팡 입력값(%) 기준으로 통일(현재ROAS·권장입력값과 같은 축). null이면 게이지 미표시.
    roasSpectrum(h) {
      if (!h || !(h.marginRate > 0)) return null
      // 목표 수익률/CPC 손잡이가 없는 타입(매출스타트·신규고객확보·기타)은 게이지 무의미
      if (!['AI광고', '매출최적화', '수동키워드', '수동스마트타겟팅'].includes(h.campaignType)) return null
      var f = h.recommendedRoasTarget > 0 ? (h.recommendedRoasInput / h.recommendedRoasTarget) : 1.1 // 쿠팡입력 환산계수
      var brk = (1 / h.marginRate) * f * 100 // 손익분기(공격 끝)
      var opt = (2 / h.marginRate) * f * 100 // 최적(순이익 최대)
      var mx = (h.S > 0 && h.cvr > 0) ? (h.S * h.cvr / 100) * f * 100 : opt * 2 // 최대효율
      if (mx <= opt) mx = opt + (opt - brk) // 안전장치
      var span = mx - brk
      var pos = v => span > 0 ? Math.max(5, Math.min(95, (v - brk) / span * 100)) : 50 // 5~95 클램프: 끝단 마커 라벨 잘림 방지
      var cur = h.currentRoasTarget || 0
      var rec = this.recInputPct(h)
      return {
        manual: this.isManualType(h.campaignType),
        brk: Math.round(brk), opt: Math.round(opt), mx: Math.round(mx),
        optPos: pos(opt),
        curVal: cur, curPos: cur ? pos(cur) : null,
        recVal: rec, recPos: rec ? pos(rec) : null,
        recCpc: this.recCpc(h)
      }
    },
    isManualType(t) { return t === '수동키워드' || t === '수동스마트타겟팅' },
    headlineStatus(h) {
      var chips = []
      if (h.learningStatus === 'LEARNING_STOPPED') chips.push({ text: '학습정체', variant: 'badge-secondary' })
      if (h.chargeGate === '충전필요') chips.push({ text: '전환 쌓기 ' + (h.conv30 || 0) + '/30', variant: 'badge-danger' })
      else if (h.chargeGate === '권장미달') chips.push({ text: '전환 쌓는 중 ' + (h.conv30 || 0) + '/60', variant: 'badge-warning' })
      if (h.stockUrgent) chips.push({ text: '재고임박', variant: 'badge-danger' })
      if (h.budgetUtilRate >= 100) chips.push({ text: '예산소진', variant: 'badge-warning' })
      if (!chips.length) chips.push({ text: '정상', variant: 'badge-light' })
      return chips
    },
    productName(vid) {
      var g = this.gdbByVid[String(vid)]
      // 계정에 따라 growthdb 이름 필드가 한글(구) 또는 쿠팡 API 원형(displayProductName/itemName)
      if (g) return g.판매상품명 || g.옵션명 || g.displayProductName || g.itemName || vid
      // 상품마스터에 없음(단종·미수집 등) — raw ID 대신 '미등록 상품' 표시. 이름 소스가 DB에 없어 재수집 시 복구
      var s = String(vid || '')
      return s.length > 4 ? '미등록 상품 (…' + s.slice(-4) + ')' : (s || '-')
    },
    chargeBadge(c30) { return c30 >= 60 ? 'badge-success' : c30 >= 30 ? 'badge-warning' : 'badge-danger' },
    productStock(p) {
      if (!p.deliveryType) return '재고데이터 없음'
      var modeMap = { '발주공격': '공격 여지', '효율보존': '재고 보존' }
      var rcov = (p.rCov === null || p.rCov === undefined) ? '' : (isFinite(p.rCov) ? ' · 재고여유 ×' + p.rCov.toFixed(1) : ' · 재고 충분')
      return p.deliveryType + (p.coverageMode ? ' · ' + (modeMap[p.coverageMode] || p.coverageMode) : '') + rcov
    },
    productRec(p, type) {
      // 전환 0이면 권장 산출 근거(CVR)가 없음 — 0%를 권장처럼 보여주지 않기
      if (this.isManualType(type)) return (p.recommendedCpc > 0) ? 'CPC ' + p.recommendedCpc + '원' : '-'
      if (!(p.recommendedRoasTarget > 0)) return '-'
      // 쿠팡 입력 기준 하나로 — 상품 단위 입력칸은 없으니 분리/제외 판단용 참고치(헤더 툴팁에 설명)
      return 'ROAS ' + Math.round(p.recommendedRoasInput * 100) + '%'
    },
    formatNum(n) {
      if (n === null || n === undefined) return '0'
      return Math.round(Number(n)).toLocaleString()
    },
    formatDate(d) {
      if (!d || d.length < 8) return d
      return d.substring(4, 6) + '/' + d.substring(6, 8)
    },
    formatChange(ch) {
      var m = { roasTarget: 'ROAS목표', budget: '예산', isActive: 'ON/OFF', 'group.pricing': 'CPC', 'group.smartTargetingPricing': '비검색CPC', 'group.keywordTargeting': '타겟팅', 'group.roasTarget': '그룹ROAS' }
      var label = m[ch.field] || ch.field
      var name = ch.campaignName ? ch.campaignName.substring(0, 6) + ' ' : ''
      if (ch.field === 'isActive') return name + label + (ch.newValue ? ' ON' : ' OFF')
      return name + label + ' ' + ch.oldValue + '→' + ch.newValue
    },
    // 엑셀 = ③ 날짜별 변경 & 추이 테이블 기준
    exportExcel() {
      if (!this.historyRows.length) {
        alert('내보낼 날짜별 데이터가 없습니다. (다음 수집 후 이용 가능)')
        return
      }
      var rows = this.historyRows.map(r => ({
        '날짜': r.date,
        '변경 내용': r.changes.map(c => this.formatChange(c)).join(', '),
        '광고비': r.cost,
        '광고매출': r.adSales,
        '자연매출': r.naturalSales,
        '전체ROAS': r.totalRoas,
        '광고ROAS': r.adRoas,
        '자연노출': r.exposure === null ? '' : r.exposure
      }))
      var ws = XLSX.utils.json_to_sheet(rows)
      var wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '날짜별추이')
      XLSX.writeFile(wb, '광고관리_날짜별추이_' + this.startDate + '_' + this.endDate + '.xlsx')
    }
  }
}
</script>

<style scoped>
/* ① 권장 액션 칩 */
.action-chip { cursor: pointer; }

/* ② SKU 영역별 매트릭스 */
.sku-area-table th, .sku-area-table td { padding: 3px 6px; font-size: 12px; }
.sku-area-table thead th { font-size: 11px; }

/* §15 ROAS/CPC 스펙트럼 게이지 */
.roas-gauge { padding: 20px 6px; }
.gauge-track {
  position: relative; height: 12px; border-radius: 6px;
  background: linear-gradient(to right, #f5b7b1 0%, #fcf3cf 50%, #abebc6 100%);
}
.gauge-opt {
  position: absolute; top: -3px; width: 2px; height: 18px;
  background: #007bff; transform: translateX(-50%);
}
.gauge-marker { position: absolute; top: 6px; }
.gauge-marker::before {
  content: ''; position: absolute; left: -6px; top: -6px;
  width: 12px; height: 12px; border-radius: 50%;
  border: 2px solid #fff; box-shadow: 0 0 2px rgba(0,0,0,.4);
}
.gauge-cur::before { background: #6c757d; }
.gauge-rec::before { background: #28a745; }
.gauge-label {
  position: absolute; transform: translateX(-50%);
  white-space: nowrap; font-size: 10px; font-weight: bold;
  padding: 1px 4px; border-radius: 3px;
}
.gauge-cur .gauge-label { top: -22px; background: #6c757d; color: #fff; }
.gauge-rec .gauge-label { top: 12px; background: #28a745; color: #fff; }

/* 모달 할 일 — 구체값 강조 */
.action-pill {
  display: inline-block; background: #343a40; color: #fff;
  font-weight: bold; font-size: 14px; padding: 4px 12px; border-radius: 5px;
}
</style>
